import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Package,
  FileText,
  Settings,
  LogOut,
  Menu,
  Plus,
  Search,
  Truck,
  Clock,
  CheckCircle,
  ChevronRight,
  MapPin,
  AlertCircle,
  Eye
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", color: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700" },
  order_placed: { label: "Order Placed", color: "bg-indigo-100 text-indigo-700" },
  processing: { label: "Processing", color: "bg-amber-100 text-amber-700" },
  in_transit: { label: "In Transit", color: "bg-blue-100 text-blue-700" },
  out_for_delivery: { label: "Out for Delivery", color: "bg-emerald-100 text-emerald-700" },
  delivered: { label: "Delivered", color: "bg-green-500 text-white" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" }
};

// Sidebar Component
const Sidebar = ({ open, setOpen }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/customer/dashboard" },
    { icon: FileText, label: "Requests", path: "/customer/requests" },
    { icon: Package, label: "Shipments", path: "/customer/shipments" },
    { icon: Settings, label: "Profile", path: "/customer/profile" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-gradient-to-b from-blue-900 to-blue-950 transform transition-transform duration-300 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-blue-800">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                Swift<span className="text-blue-300">Logix</span>
              </span>
            </Link>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-blue-800">
            <p className="text-blue-200 text-xs uppercase tracking-wider">Welcome</p>
            <p className="text-white font-medium truncate">{user?.name}</p>
            {user?.company_name && (
              <p className="text-blue-300 text-sm truncate">{user.company_name}</p>
            )}
          </div>

          {/* Nav Links */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive 
                      ? 'bg-white/20 text-white' 
                      : 'text-blue-200 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`customer-sidebar-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-blue-800">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-sm text-blue-200 hover:bg-white/10 hover:text-white transition-colors"
              data-testid="customer-logout-button"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Home Component
const CustomerDashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/customer/dashboard`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: "Total Requests", value: stats?.total_requests || 0, icon: FileText, color: "bg-purple-500" },
    { label: "Pending Requests", value: stats?.pending_requests || 0, icon: Clock, color: "bg-amber-500" },
    { label: "Active Shipments", value: stats?.active_shipments || 0, icon: Truck, color: "bg-blue-500" },
    { label: "Delivered", value: stats?.delivered_shipments || 0, icon: CheckCircle, color: "bg-green-500" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-dashboard-home">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Dashboard</h1>
        <p className="text-slate-500">Overview of your shipping activity</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-sm border border-slate-200 p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{stat.label}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-sm flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Quick Actions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/customer/requests">
            <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 rounded-sm">
              <Plus className="w-6 h-6 text-blue-600" />
              <span>Request New Shipment</span>
            </Button>
          </Link>
          <Link to="/customer/shipments">
            <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 rounded-sm">
              <Package className="w-6 h-6 text-blue-600" />
              <span>View My Shipments</span>
            </Button>
          </Link>
          <Link to="/tracking">
            <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 rounded-sm">
              <Search className="w-6 h-6 text-blue-600" />
              <span>Track a Shipment</span>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Shipment Requests Component
const ShipmentRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    pickup_address: "",
    delivery_address: "",
    package_description: "",
    weight: "",
    service_type: "standard",
    pickup_date: "",
    special_instructions: ""
  });

  const fetchRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customer/shipment-requests`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/customer/shipment-requests`, {
        ...formData,
        weight: parseFloat(formData.weight)
      }, { withCredentials: true });
      toast.success("Shipment request submitted successfully!");
      setIsDialogOpen(false);
      setFormData({
        pickup_address: "",
        delivery_address: "",
        package_description: "",
        weight: "",
        service_type: "standard",
        pickup_date: "",
        special_instructions: ""
      });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-requests-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Shipment Requests</h1>
          <p className="text-slate-500">Request new shipments and track their status</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-blue-600 hover:bg-blue-700" data-testid="new-request-button">
              <Plus className="w-4 h-4 mr-2" /> New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Request New Shipment</DialogTitle>
              <DialogDescription>Fill in the details for your shipment request.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Pickup Address *</Label>
                <Input
                  placeholder="123 Main St, City, State, ZIP"
                  value={formData.pickup_address}
                  onChange={(e) => setFormData({...formData, pickup_address: e.target.value})}
                  required
                  data-testid="request-pickup-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Delivery Address *</Label>
                <Input
                  placeholder="456 Oak Ave, City, State, ZIP"
                  value={formData.delivery_address}
                  onChange={(e) => setFormData({...formData, delivery_address: e.target.value})}
                  required
                  data-testid="request-delivery-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Package Description *</Label>
                  <Input
                    placeholder="Electronics, Documents, etc."
                    value={formData.package_description}
                    onChange={(e) => setFormData({...formData, package_description: e.target.value})}
                    required
                    data-testid="request-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weight (kg) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="5.0"
                    value={formData.weight}
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                    required
                    data-testid="request-weight-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service Type</Label>
                  <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="express">Express</SelectItem>
                      <SelectItem value="freight">Freight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Preferred Pickup Date</Label>
                  <Input
                    type="date"
                    value={formData.pickup_date}
                    onChange={(e) => setFormData({...formData, pickup_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Special Instructions</Label>
                <Textarea
                  placeholder="Any special handling requirements..."
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-request-button">Submit Request</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Request ID</TableHead>
                <TableHead>Pickup</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tracking ID</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="spinner w-6 h-6 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No shipment requests yet. Create your first request!
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => (
                  <TableRow key={req.request_id}>
                    <TableCell className="font-medium">{req.request_id}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{req.pickup_address}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{req.delivery_address}</TableCell>
                    <TableCell className="capitalize">{req.service_type}</TableCell>
                    <TableCell>
                      <Badge className={statusConfig[req.status]?.color}>
                        {statusConfig[req.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {req.tracking_id ? (
                        <Link to={`/tracking?id=${req.tracking_id}`} className="text-blue-600 hover:underline">
                          {req.tracking_id}
                        </Link>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// Customer Shipments Page
const CustomerShipmentsPage = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const response = await axios.get(`${API}/customer/shipments`, { withCredentials: true });
        setShipments(response.data);
      } catch (error) {
        toast.error("Failed to load shipments");
      } finally {
        setLoading(false);
      }
    };
    fetchShipments();
  }, []);

  return (
    <div className="space-y-6" data-testid="customer-shipments-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>My Shipments</h1>
        <p className="text-slate-500">Track all your active and past shipments</p>
      </div>

      {/* Shipments Table */}
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tracking ID</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="spinner w-6 h-6 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : shipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No shipments yet. Your approved requests will appear here.</p>
                  </TableCell>
                </TableRow>
              ) : (
                shipments.map((shipment) => (
                  <TableRow key={shipment.tracking_id}>
                    <TableCell className="font-medium">{shipment.tracking_id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span>{shipment.origin}</span>
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                        <span>{shipment.destination}</span>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{shipment.service_type}</TableCell>
                    <TableCell>
                      <Badge className={statusConfig[shipment.status]?.color}>
                        {statusConfig[shipment.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {shipment.current_location}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link to={`/tracking?id=${shipment.tracking_id}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4 mr-1" /> Track
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// Profile Page
const CustomerProfilePage = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-6" data-testid="customer-profile-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Profile</h1>
        <p className="text-slate-500">Your account information</p>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-6">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-slate-500">Full Name</Label>
            <p className="font-medium text-slate-900">{user?.name}</p>
          </div>
          <div>
            <Label className="text-slate-500">Email</Label>
            <p className="font-medium text-slate-900">{user?.email}</p>
          </div>
          <div>
            <Label className="text-slate-500">Company</Label>
            <p className="font-medium text-slate-900">{user?.company_name || "Not specified"}</p>
          </div>
          <div>
            <Label className="text-slate-500">Phone</Label>
            <p className="font-medium text-slate-900">{user?.phone || "Not specified"}</p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-slate-500">Address</Label>
            <p className="font-medium text-slate-900">{user?.address || "Not specified"}</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <p className="text-sm text-blue-800">
          Need to update your profile? Contact our support team at <a href="mailto:support@swiftlogix.com" className="font-medium underline">support@swiftlogix.com</a>
        </p>
      </div>
    </div>
  );
};

// Main Customer Dashboard Component
const CustomerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              className="lg:hidden p-2 text-slate-600"
              onClick={() => setSidebarOpen(true)}
              data-testid="customer-mobile-sidebar-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-4 ml-auto">
              <Link to="/tracking">
                <Button variant="outline" size="sm" className="rounded-sm">
                  <Search className="w-4 h-4 mr-2" /> Track Shipment
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Routes>
            <Route path="dashboard" element={<CustomerDashboardHome />} />
            <Route path="requests" element={<ShipmentRequestsPage />} />
            <Route path="shipments" element={<CustomerShipmentsPage />} />
            <Route path="profile" element={<CustomerProfilePage />} />
            <Route path="*" element={<CustomerDashboardHome />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default CustomerDashboard;
